import { type FC } from "react";

import { type QueryBuilderState } from "~/components/canvas/easel/query-builder/QueryBuilderState";
import { type QueryBuilderStateManager } from "~/components/canvas/easel/query-builder/QueryBuilderStateManager";
import { EmptySlots } from "~/components/inspector/expandable/EmptySlots";

import { type ValueInspectorRowInfo } from "../traverseValue/ValueInspectorRowInfo";
import { type ValuePath } from "../traverseValue/ValuePath";
import { CollapsedShallowRowValue } from "./CollapsedShallowRowValue";
import { NonExpandableValue } from "./NonExpandableValue";
import { ValueName } from "./ValueName";

/**
 * The value displayed in some inspector row: a string, an object, etc. For example, in the
 * following object, the values displayed are `a: "b",` and `c: { d: "e" },`.
 *
 *     v myCell = {
 *         a: "b",
 *     >   c: { d: "e" },
 *       }
 *
 * Note that this component does NOT include the expand/collapse caret, the indentation, or any of
 * the other chrome that's required for this value to make sense in the context where the value is
 * nested inside an object.
 */
export const InspectorRowValue: FC<{
	cellID: string;
	row: ValueInspectorRowInfo;
	queryBuilderState: QueryBuilderState | null;
	queryBuilderStateManager: QueryBuilderStateManager;
}> = ({ cellID, row, queryBuilderState, queryBuilderStateManager }) => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const lastComponent = row.path[row.path.length - 1]!;

	let punctuation: JSX.Element;
	switch (lastComponent.type) {
		case "Cell":
			punctuation = <>&nbsp;=&nbsp;</>;
			break;
		case "ArrayElement":
		case "SetField":
		case "ObjectField":
			punctuation = <>:&nbsp;</>;
			break;
		case "MapField":
			punctuation = <>&nbsp;{"=>"}&nbsp;</>;
			break;
	}

	switch (row.type) {
		case "NonExpandableValueRow":
			//
			// Non-expandable fields will not have the "expand/contract" caret. They will typically
			// look something like the field `a` in the following object.
			//
			//     v myCell = {
			//         a: "b",
			//     >   c: { d: "e" },
			//       }
			//

			return (
				<>
					<ValueName
						cellID={cellID}
						row={row}
						queryBuilderState={queryBuilderState}
						queryBuilderStateManager={queryBuilderStateManager}
					/>
					{punctuation}
					<NonExpandableValue typeInfo={row.valueTypeInfo} />
				</>
			);
		case "CollapsedValueRow":
			//
			// Collapsed fields will generally have an "expand/contract" caret, and a small summary
			// of the data inside. They will typically look something like `c` in the following
			// object.
			//
			//     v myCell = {
			//         a: "b",
			//     >   c: { d: "e" },
			//       }
			//

			return (
				<>
					<ValueName
						cellID={cellID}
						row={row}
						queryBuilderState={queryBuilderState}
						queryBuilderStateManager={queryBuilderStateManager}
					/>
					{punctuation}
					{constructorOpener(lastComponent)}
					<CollapsedShallowRowValue row={row} />
					{constructorCloser(lastComponent)}
				</>
			);
		case "ExpandedValueOpeningRow":
			//
			// Expanded fields have an "opening" row which contains an "expand/contract" caret,
			// followed by some "opening" punctuation like `{` or `[`. For example, lines 1 and 3 in
			// this value are "opening" rows:
			//
			//     v myCell = {
			//         a: "b",
			//     v   c: {
			//           d: "e",
			//         },
			//       }
			//

			return (
				<>
					<ValueName
						cellID={cellID}
						row={row}
						queryBuilderState={queryBuilderState}
						queryBuilderStateManager={queryBuilderStateManager}
					/>
					{punctuation}
					{constructorOpener(lastComponent)}
				</>
			);
		case "ExpandedValueClosingRow":
			//
			// Expanded field "closers" are usually just a piece of punctuation that closes out an
			// object, like `}` or `]`. In the following object, the last and second-to-last lines
			// are "closing" rows".
			//
			//     v myCell = {
			//         a: "b",
			//     v   c: {
			//           d: "e",
			//         },
			//       }
			//

			return <>{constructorCloser(lastComponent)}</>;
		case "EmptyArraySlotValueRow":
			//
			// An empty slot row shows indexes in an array where there is no value. For example:
			//
			//     v myCell = [
			//         0: empty,
			//     >   1: { a: "b" },
			//         2: empty,
			//       ]
			//

			return (
				<>
					<ValueName
						cellID={cellID}
						row={row}
						queryBuilderState={queryBuilderState}
						queryBuilderStateManager={queryBuilderStateManager}
					/>
					{punctuation} <EmptySlots n={row.emptySlots} />
				</>
			);
	}
};

export const constructorOpener = (value: ValuePath[number]) => {
	switch (value.typeInfo.typeTag) {
		case "Arrayish":
			return `Array(${(value.value as Array<never>).length || 0})[`;
		case "Map":
			return `Map(${(value.value as Map<never, never>).size || 0}){`;
		case "Set":
			return `Set(${(value.value as Set<never>).size || 0}){`;
		default:
			return "{";
	}
};

const constructorCloser = (value: ValuePath[number]) => {
	switch (value.typeInfo.typeTag) {
		case "Arrayish":
			return "]";
		default:
			return "}";
	}
};
